import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function BitcoinTechPage85() {
  const contentRef = useRef(null);
  const titleText = "Mempool and Transaction Relay Policies";
  const links = {
    prev: "/bitcoin-tech-page-84",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-86",
  };

  const questions = [
    {
      text: "1. What is a mempool?",
      options: [
        { value: "A", label: "A. A global database controlled by miners" },
        {
          value: "B",
          label:
            "B. A temporary holding area where unconfirmed transactions wait before being included in a block",
        },
        {
          value: "C",
          label: "C. A backup of the blockchain stored by exchanges",
        },
        {
          value: "D",
          label: "D. A pool used only for storing invalid transactions",
        },
      ],
      correct: "B",
    },
    {
      text: "2. Why do different Bitcoin nodes have different mempools?",
      options: [
        {
          value: "A",
          label:
            "A. Because there is a central authority that assigns transactions selectively",
        },
        {
          value: "B",
          label:
            "B. Because each node maintains its own mempool based on local policies, connectivity, and timing",
        },
        { value: "C", label: "C. Because miners rewrite mempool data" },
        {
          value: "D",
          label: "D. Because mempools are synchronized only once per day",
        },
      ],
      correct: "B",
    },
    {
      text: "3. What primarily determines transaction priority within the mempool?",
      options: [
        { value: "A", label: "A. The age of the transaction" },
        { value: "B", label: "B. The sender’s wallet software" },
        {
          value: "C",
          label: "C. The transaction fee offered to miners",
        },
        { value: "D", label: "D. The size of the transaction output" },
      ],
      correct: "C",
    },
    {
      text: "4. What is an orphan transaction?",
      options: [
        {
          value: "A",
          label:
            "A. A transaction that has been permanently rejected by the network",
        },
        {
          value: "B",
          label:
            "B. A transaction whose parent transaction has not yet been seen by the node",
        },
        { value: "C", label: "C. A transaction with zero fees" },
        { value: "D", label: "D. A transaction mined outside the blockchain" },
      ],
      correct: "B",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={85}
        timerMinutes={60}
        currentLevel={6}
        unlockedBadges={11}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 85</b>! Today’s lesson takes you deep into the
        behind-the-scenes mechanics of how Bitcoin transactions move through the
        network. You’ll explore the mempool, transaction relay policies, and the
        role of node sovereignty, uncovering how unconfirmed transactions
        compete, propagate, and eventually reach miners. This lesson highlights
        how Bitcoin’s decentralized design replaces centralized control with
        local decision-making, economic incentives, and resilience.
        <br />
        <br />
        <b>Understanding Mempool</b>
        <br />
        <br />
        At the heart of Bitcoin’s transaction flow lies a temporary holding area
        known as mempool. Mempool, short for memory pool, is where unconfirmed
        transactions reside while waiting to be included in a block. Every node
        in the Bitcoin network maintains its own mempool independently, and
        while they serve a similar purpose, no two mempools have to be
        identical. A transaction may appear in the mempool of one node but be
        absent from another depending on differences in connectivity, relay
        policies, and the timing of transaction propagation. This decentralized
        design ensures that there is no global or authoritative mempool, but
        rather a collection of distributed views of the pending transaction set.
        The mempool acts as a dynamic and ever changing environment where
        transactions compete for miner attention, with fees serving as the
        primary signal of priority. Because blocks are limited in size and
        therefore cannot include every transaction at once, the mempool becomes
        the staging ground where economic incentives play out.
        <br />
        For a transaction to enter a node’s mempool, it must pass a rigorous set
        of checks. First, the transaction must be syntactically valid, which
        includes having properly formatted inputs and outputs, adhering to size
        limits, and carrying signatures that verify against the public keys
        provided. It must also pass contextual validation, which ensures that
        its inputs refer to existing unspent outputs, that those outputs are not
        already spent in the mempool or blockchain, and that the transaction
        pays an appropriate fee. Transactions that do not offer at least the
        minimum relay fee set by a node are typically rejected to conserve
        bandwidth and memory. Once admitted, the transaction lives in the
        mempool until it is either confirmed in a block, replaced by a higher
        fee transaction that spends the same inputs, or evicted due to memory
        pressure or conflicts. Transactions may also be removed if they become
        invalid due to a chain reorganization or if one of their parent
        transactions fails to confirm. The mempool is not static but rather a
        continuously evolving set of data shaped by the network’s collective
        policies and local decisions.
        <br />
        <br />
        <b>Relay Policies and the Sovereignty of Node Operators</b>
        <br />
        <br />
        Transaction relay is the process by which nodes share unconfirmed
        transactions with one another, ensuring that transactions propagate
        through the network and reach miners for potential inclusion in a block.
        This process is governed by a combination of default relay policies
        embedded in Bitcoin clients and custom policies chosen by node
        operators. Relay policies determine whether a node will forward a
        transaction to its peers or discard it. Importantly, these policies
        exist at the discretion of each node operator, reinforcing the principle
        of sovereignty in Bitcoin’s architecture. The network has no mechanism
        to force a node to relay any given transaction. Instead, nodes act
        independently, applying their own rules about what is worth relaying.
        <br />
        A central distinction in transaction relay lies between standard and non
        standard transactions. Standard transactions follow common script
        templates such as pay to public key hash or pay to witness public key
        hash, and they respect resource limits that have been widely accepted by
        the network. These transactions are readily relayed by default policies
        because they are efficient, well understood, and broadly compatible. Non
        standard transactions, on the other hand, may use unusual or complex
        scripts that fall outside common patterns. While these transactions are
        still valid under consensus rules, many nodes will refuse to relay them
        in order to prevent bandwidth waste or potential abuse. This does not
        mean such transactions cannot be mined. A miner who receives a non
        standard transaction directly could still include it in a block if it
        passes consensus rules, but its path to a miner is less direct because
        relay policies restrict its movement across the network.
        <br />
        Concrete examples help illustrate the role of relay policies. The
        minimum relay fee is one of the most important parameters. By default,
        Bitcoin nodes enforcing standard policy rules will not relay
        transactions that offer fees below this threshold. This prevents the
        network from being overwhelmed by low value spam transactions that would
        otherwise clog memory and bandwidth. Another example is the restriction
        on the size of the data field in an OP_RETURN output. OP_RETURN allows
        embedding arbitrary data in the blockchain, but nodes enforce a size
        limit on this data to prevent abuse. These restrictions are not acts of
        censorship but expressions of the autonomy of each node runner. Every
        node operator has the right to decide which transactions they wish to
        relay, and others have the right to make different choices. This
        pluralism is a safeguard, as it prevents any single actor from dictating
        network behavior and preserves the decentralized ethos of Bitcoin.
        <br />
        <br />
        <div class="policy-card">
          <h1 class="policy-title">Relay &amp; Mining Policy Defaults</h1>
          <p class="policy-lead">
            This table lists common relay and mining policy options and their
            default values in Bitcoin Knots v28.1
          </p>

          <div class="policy-table-wrapper">
            <table
              class="policy-table"
              aria-label="Bitcoin Knots v28.1 policy defaults"
            >
              <thead>
                <tr>
                  <th>Policy / Option</th>
                  <th>Default Value</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <span class="tech-param">-acceptnonstdtxn</span>
                  </td>
                  <td class="tech-value">0 (testnet: 1)</td>
                  <td>Relay and mine non-standard transactions</td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">acceptnonstddatacarrier</span>
                  </td>
                  <td class="tech-value">0</td>
                  <td>Relay and mine non-OP_RETURN data-carrier injection</td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-bytespersigop</span>
                  </td>
                  <td class="tech-value">20</td>
                  <td>
                    Equivalent bytes per sigop in transactions for relay and
                    mining
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-bytespersigopstrict</span>
                  </td>
                  <td class="tech-value">20</td>
                  <td>
                    Minimum bytes per sigop in transactions relayed and mined
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-datacarrier</span>
                  </td>
                  <td class="tech-value">1</td>
                  <td>Relay and mine OP_RETURN data-carrier transactions</td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-datacarriercost</span>
                  </td>
                  <td class="tech-value">1</td>
                  <td>
                    Treat extra data in transactions as at least N vbytes per
                    actual byte
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-datacarriersize</span>
                  </td>
                  <td class="tech-value">42</td>
                  <td>
                    Maximum size (bytes) of data in OP_RETURN transactions
                    relayed and mined
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-maxscriptsize</span>
                  </td>
                  <td class="tech-value">1650</td>
                  <td>Maximum size (bytes) of scripts relayed and mined</td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-mempoolfullrbf</span>
                  </td>
                  <td class="tech-value">1</td>
                  <td>
                    Accept Replace-By-Fee without requiring opt-in signaling
                    (full RBF)
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-mempoolreplacement</span>
                  </td>
                  <td class="tech-value">fee,-optin</td>
                  <td>
                    Replacement policy: 0 disables RBF; fee,optin respects
                    opt-out; fee,-optin always allows RBF
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-mempooltruc</span>
                  </td>
                  <td class="tech-value">accept</td>
                  <td>
                    Behavior for TRUC transactions: reject, accept, or enforce
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-minrelaytxfee</span>
                  </td>
                  <td class="tech-value">0.00001 BTC/kvB</td>
                  <td>
                    Minimum relay fee rate; fees smaller are treated as zero
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-permitbaremultisig</span>
                  </td>
                  <td class="tech-value">0</td>
                  <td>Relay bare (non-P2SH) multisig transactions</td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-permitbarepubkey</span>
                  </td>
                  <td class="tech-value">0</td>
                  <td>Relay legacy bare pubkey outputs</td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-rejectparasites</span>
                  </td>
                  <td class="tech-value">1</td>
                  <td>Refuse to relay or mine parasitic overlay protocols</td>
                </tr>
                <tr>
                  <td>
                    <span class="tech-param">-spkreuse</span>
                  </td>
                  <td class="tech-value">allow</td>
                  <td>
                    Accept transactions that reuse addresses or pubkey scripts
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <b> The Lifecycle of Transactions in the Network</b>
        <br />
        <br />
        Once a transaction enters the mempool, its journey through the network
        depends heavily on the relay process. When a node accepts a transaction
        into its mempool, it announces its existence to peers by sending an
        inventory message that contains the transaction’s hash. Interested peers
        respond by requesting the full transaction data, after which they
        validate it and, if acceptable, admit it into their own mempools. This
        process repeats across the network, creating a web of propagation that
        ensures miners eventually see the transaction. The speed of propagation
        is influenced by the network topology, the number of peers each node
        connects to, and the transaction’s fee level. Transactions with higher
        fees are often prioritized both by relay and by miners, giving them a
        faster path to confirmation.
        <br />
        Transactions do not remain in the mempool forever. They are removed once
        they are confirmed in a block, as their inputs are no longer unspent.
        They may also be replaced by transactions spending the same inputs but
        offering higher fees through the replace by fee mechanism, which
        incentivizes miners to include the higher paying transaction. In times
        of congestion, when the mempool exceeds its maximum size, nodes evict
        lower fee transactions to make space for higher fee ones, effectively
        creating a fee market. Additionally, transactions can be removed if a
        chain reorganization invalidates them or if one of their parent
        transactions fails to confirm. These mechanisms maintain the integrity
        and efficiency of the mempool, ensuring that it serves as an effective
        buffer between transaction creation and block inclusion.
        <br />
        The decentralized nature of mempools creates an interesting dynamic. A
        transaction may be relayed quickly through some parts of the network
        while being ignored in others due to differing relay policies. There is
        no guarantee that every node will see every transaction. This
        variability highlights the importance of diversity in connections and
        underscores the fact that there is no universal mempool. Instead, each
        node has its own local perspective, and the system functions by
        overlapping these perspectives into a resilient network that achieves
        eventual consistency.
        <br />
        <br />
      </p>
      <img src="./relay.png" width="700"></img> <br />
      <br />
      <p className="content">
        <b>Unconfirmed Transactions & their Ancestors-Descendents</b>
        <br />
        <br />
        Unconfirmed transactions are transactions that have been created and
        broadcast by users but are still waiting to be included in a block by
        miners. They sit in the mempool of each node, representing a temporary
        holding area for pending transactions. Since Bitcoin transactions can
        spend outputs of earlier transactions, it is entirely possible for one
        unconfirmed transaction to depend on another that is also unconfirmed.
        This gives rise to the concepts of ancestors and descendants within the
        mempool. An ancestor is any transaction that must confirm before a given
        transaction can confirm, while a descendant is any transaction that
        depends on a given transaction being confirmed first. For example, if
        Alice pays Bob with a transaction that is still unconfirmed, and Bob
        immediately spends those coins in a new transaction to Carol, then
        Alice’s transaction is an ancestor of Bob’s, and Bob’s is a descendant
        of Alice’s. If Carol then spends her unconfirmed output further, the
        chain of dependencies continues to grow.
        <br />
        To prevent abuse of this mechanism and to keep node resources under
        control, Bitcoin clients enforce strict limits on how large these
        dependency chains can become. The mempool policy sets a maximum of 25
        unconfirmed ancestors for any transaction and a maximum of 25
        descendants for any transaction. This means that if a user attempts to
        broadcast a transaction that would push the number of ancestors above
        25, the transaction will not be accepted into the mempool. Likewise, if
        a transaction already in the mempool would end up with more than 25
        descendants after accepting a new transaction, the new one will be
        rejected. These limits are applied consistently across the network,
        ensuring that even though each node maintains its own mempool, the
        propagation of excessively long chains is naturally restricted.
        <br />
        The eviction and rejection rules associated with these limits are
        important for understanding how nodes maintain healthy mempools. When a
        transaction is evaluated for entry, the node calculates its ancestry and
        descendant sets. If including it would violate the 25 ancestor or
        descendant rule, it is simply not admitted into the mempool at all.
        Transactions already in the mempool are not evicted just because of
        these limits, but they can be indirectly removed if the transaction they
        depend on is dropped, replaced, or conflicted out. For example, if an
        ancestor transaction is evicted because it fails to meet fee
        requirements or is replaced by a higher-fee alternative, its descendants
        will also be removed because they are no longer valid without their
        parent.
        <br />
        These rules serve a dual purpose. On the one hand, they protect nodes
        from having to store and manage arbitrarily large chains of related
        unconfirmed transactions, which could otherwise be used as a vector for
        memory exhaustion attacks. On the other hand, they also encourage users
        to consolidate transactions or wait for confirmations rather than
        endlessly chaining unconfirmed spends. While these limits can
        occasionally inconvenience users who attempt to construct complex
        transaction trees, they are a critical safeguard for the health of the
        network. By capping ancestors and descendants at 25 each, Bitcoin
        strikes a balance between usability and resilience, allowing reasonable
        flexibility in transaction chaining while preventing unbounded growth
        that would otherwise degrade node performance and network stability.
        <br />
        <br />
        <b>Orphan Transactions</b>
        <br />
        <br />
        An orphan transaction is a special case within Bitcoin’s transaction
        relay process, arising when a node receives a transaction that it cannot
        yet fully validate because the parent transaction it depends on has not
        been seen. Since every Bitcoin transaction spends outputs from earlier
        ones, validation requires the parent’s presence in the node’s UTXO set
        or at least in its mempool. If a child transaction arrives first and its
        parent has not yet been relayed or confirmed, the node cannot verify
        whether the inputs actually exist or are already spent. Instead of
        discarding such a transaction, the node temporarily holds it in a
        separate orphan pool, awaiting the missing parent. When the parent
        transaction eventually arrives, the node re-checks the orphan, and if
        the inputs are valid, it is then promoted into the mempool like any
        other unconfirmed transaction.
        <br />
        The existence of orphan transactions is usually the result of natural
        network conditions. Nodes are connected in a peer-to-peer topology where
        messages can travel along different routes and at slightly different
        speeds. This means that in some cases a child transaction can propagate
        through the network faster than its parent, creating a temporary gap in
        knowledge for some nodes. Orphans may also appear when users
        deliberately broadcast transactions out of order, whether by accident in
        their wallet software or for experimental purposes. While orphan
        transactions are not invalid, they are incomplete from the perspective
        of the receiving node, which explains why they cannot immediately join
        the mempool.
        <br />
        To prevent the orphan pool from becoming a burden or an avenue for
        denial-of-service attacks, Bitcoin Core sets practical limits. Only a
        finite number of orphan transactions are kept, and each is subject to
        eviction policies if the pool fills up. This ensures that nodes cannot
        be overwhelmed with useless or incomplete data. Once the missing parent
        transaction is received, the orphan pool is re-checked, and valid
        children are released into the mempool where they can begin the normal
        journey toward inclusion in a block. If the parent never arrives or is
        invalid, the orphan transaction is eventually discarded.
        <br />
        Although the term “orphan” may suggest permanent abandonment, in most
        cases these transactions quickly resolve themselves once the parent
        arrives. The mechanism is essentially a safeguard that ensures the
        network remains robust in the face of variable propagation speeds and
        ordering. By maintaining a small buffer of orphan transactions, nodes
        avoid losing potentially valid data while still protecting themselves
        against resource exhaustion. This balance allows Bitcoin to handle
        out-of-sequence transaction broadcasts gracefully, reinforcing the
        resilience of its decentralized peer-to-peer architecture.
        <br />
        <br />
        <br />
        <br />
        <b>Out of Band Transactions and Direct Miner Communication</b>
        <br />
        <br />
        While the default path for transactions is to spread through the mempool
        via transaction relay, there exists another method known as out of band
        transaction relay. Out of band transactions bypass the mempool
        propagation process by being transmitted directly from a user to a
        miner. This can occur through private agreements, specialized channels,
        or direct submissions to mining pools. In such cases the transaction may
        never appear in the mempool of most nodes but still find its way into a
        block if the miner chooses to include it. Out of band transactions are
        often used for privacy reasons, to avoid broadcasting sensitive
        information widely, or for efficiency, such as ensuring that a high
        value transaction is delivered directly to the entities most capable of
        confirming it.
        <br />
        The existence of out of band transactions underscores the flexibility of
        Bitcoin’s architecture. Relay through the mempool is the default because
        it maximizes redundancy and decentralization, but nothing in the
        protocol prevents participants from choosing alternative methods. This
        again reflects the sovereignty of network participants. Just as node
        operators have the right to choose which transactions they will relay,
        users have the right to choose how they will broadcast their
        transactions. Whether through the mempool or through direct miner
        communication, the key point is that Bitcoin provides multiple pathways
        to confirmation, and no single pathway can be monopolized or controlled.
        <br />
        Understanding out of band transactions also helps to dispel
        misconceptions about censorship in Bitcoin. Even if certain transactions
        face resistance in mempool propagation due to local relay policies,
        users can still reach miners by alternative means. Since miners
        ultimately decide what enters the blockchain based on consensus rules
        rather than relay policies, the system maintains a clear separation
        between what is valid and what is relayed. This separation protects the
        protocol from attempts at control while giving participants the tools to
        operate according to their own preferences and strategies.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        In this lesson, we examined how the mempool functions as a decentralized
        staging area for unconfirmed transactions, governed by validation rules,
        fee markets, and eviction policies. We explored relay policies, standard
        vs. non-standard transactions, ancestor and descendant limits, orphan
        transactions, and out-of-band transaction relay. Together, these
        mechanisms demonstrate how Bitcoin balances efficiency, security, and
        decentralization, ensuring that no single actor controls transaction
        flow while maintaining a robust and censorship-resistant network.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}
      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage85;
